import {
	makeApiRequest,
	Auth,
	ApiRequestGetParams,
	ApiRequestGetParamsRaw,
	isApiRequestGetParams
} from "api/makeApiRequest";
import { ValidationCallback } from "validation/validate";
import { SearchResults } from "types-and-validators/SearchResults";
import { addPostProcessingToResponse } from "api/utils/addPostProcessingToResponse";
import { sorted } from "utils/sorted";
import { flagValidationErrorsInSearchResults } from "api/makeApiRequestType/flagValidationErrorsInSearchResults";

type PostProcessor<RawApiOutput, ProcessedApiOutput> = (
	arg: RawApiOutput,
	requestPayload: ApiRequestGetParamsRaw
) => ProcessedApiOutput;

export type Params<RawQueryData, RawApiOutput, ProcessedApiOutput> = {
	endPoint: string;
	flagValidationErrorsInRow: ValidationCallback<RawApiOutput>;
	preProcessor?: (arg: RawQueryData) => ApiRequestGetParams;
	postProcessor?: PostProcessor<RawApiOutput, ProcessedApiOutput>;
	sort?: (a: ProcessedApiOutput, b: ProcessedApiOutput) => number;
};

export const makePaginatedSearch = <
	RawQueryData extends { [k: string]: string | unknown | null | Date },
	RawApiOutput,
	ProcessedApiOutput = RawApiOutput
>({
	endPoint,
	flagValidationErrorsInRow,
	preProcessor,
	postProcessor,
	sort = (a, b) => 1
}: Params<RawQueryData, RawApiOutput, ProcessedApiOutput>) => (
	data: RawQueryData,
	auth: Auth,
	cacheInMemory: boolean = false
) => {
	const flagValidationErrors: ValidationCallback<
		SearchResults<RawApiOutput>
	> = (input, utils, auth) => {
		flagValidationErrorsInSearchResults(
			flagValidationErrorsInRow,
			input,
			utils,
			auth
		);
	};

	const get = preProcessor ? preProcessor(data) : data;
	if (!isApiRequestGetParams(get)) {
		throw new Error(
			"Invalid query data - consider adding a preProcessor to convert it to the correct format"
		);
	}

	const request = makeApiRequest(endPoint, {
		auth,
		cacheInMemory,
		data: {
			get
		},
		flagValidationErrors
	});

	const fullPostProcessor = (
		response: SearchResults<RawApiOutput> | undefined,
		requestPayload: ApiRequestGetParamsRaw
	) => {
		const { page, nextPage, prevPage, ...rest } = response
			? response
			: { page: undefined, nextPage: undefined, prevPage: undefined };

		const output: SearchResults<ProcessedApiOutput> = {
			...rest,
			page: page
				? sorted(
						page.map(row =>
							postProcessor
								? postProcessor(row, requestPayload)
								: ((row as unknown) as ProcessedApiOutput)
						),
						sort
				  )
				: [],
			nextPage: nextPage
				? sorted(
						nextPage.map(row =>
							postProcessor
								? postProcessor(row, requestPayload)
								: ((row as unknown) as ProcessedApiOutput)
						),
						sort
				  )
				: [],
			prevPage: prevPage
				? sorted(
						prevPage.map(row =>
							postProcessor
								? postProcessor(row, requestPayload)
								: ((row as unknown) as ProcessedApiOutput)
						),
						sort
				  )
				: []
		};

		return output;
	};
	return addPostProcessingToResponse(request, fullPostProcessor, get);
};
